import { useCallback, useEffect, useState } from "react";
import axios from "axios";

import LoadingSvg2 from "./LoadingSvg2";
import OpenEnrollmentDao from "@dao/OpenEnrollmentDao";
import SchoolCard from "./SchoolCard";
import T from "./translations/Translations";

import { useGlobalContext } from "./contextProvider/ContextProvider";

import type { AppObject, SchoolChoice } from "@/types/GlobalState";

// This interface represents the actual, more complex shape of a student's
// data in the `studentAppObjects` state, which the global types don't fully capture.
interface StudentAppData {
    schoolChoices?: SchoolChoice[];
    [key: string]: { appObj: AppObject | null } | SchoolChoice[] | undefined;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface OeStudent {
    key?: string | number;
    schoolChoiceDetailDtos?: SchoolChoice[];
    [key: string]: unknown;
}

interface ExpandInfoProps {
    setExpand: ((val: boolean) => void) | null;
    student: OeStudent;
}

// ─── Module-level helpers ─────────────────────────────────────────────────────

const fetchParticipatingSchools = async (
    schoolChoiceDetailDtos: SchoolChoice[],
    dispatchAppObject: (arr: SchoolChoice[]) => void,
    signal: AbortSignal
): Promise<void> => {
    try {
        const schoolPromises = schoolChoiceDetailDtos.map(async (schoolChoice) => {
            const response = await OpenEnrollmentDao<{ payload: Partial<SchoolChoice> }>({
                action: "participatingSchoolRead",
                key: schoolChoice.participatingSchoolId,
                params: { fetchLocationData: true },
                signal
            });
            if (!response?.data?.payload) return null;
            return { ...schoolChoice, ...response.data.payload };
        });

        const participatingSchools = (await Promise.all(schoolPromises)).filter(
            (s): s is SchoolChoice => s !== null && s !== undefined
        );

        if (signal.aborted) return;

        participatingSchools.sort((a, b) =>
            Number.parseInt(String(a.priority), 10) > Number.parseInt(String(b.priority), 10) ? 1 : -1
        );
        dispatchAppObject(participatingSchools);
    } catch (error: unknown) {
        // Don't log an error if the request was intentionally canceled
        if (axios.isCancel(error)) {
            // This is an expected event in React 18 Strict Mode, log as info not warning.
            console.warn("School choice fetch canceled.");
        } else {
            console.error("Unable to retrieve Open Enrollment details:\n", error);
        }
    }
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Display expanded school choice information for a student
 * @name ExpandInfo
 */
const ExpandInfo = ({ setExpand, student }: ExpandInfoProps): React.JSX.Element => {
    const { dispatch, state } = useGlobalContext();
    const { lang, studentAppObjects } =
        (state as { lang?: string; studentAppObjects?: Record<string, Partial<StudentAppData>> }) || {};

    const [loading, setLoading] = useState(true);
    const [schoolChoices, setSchoolChoices] = useState<SchoolChoice[]>([]);

    const studentKey = student?.key as string | undefined;
    const existing = studentKey
        ? ((studentAppObjects?.[studentKey]?.schoolChoices as SchoolChoice[] | null) ?? null)
        : null;
    const schoolChoiceDetailDtos = student?.schoolChoiceDetailDtos;

    const dispatchAppObject = useCallback(
        (arr: SchoolChoice[]) => {
            if (!studentKey || !dispatch) return;

            const currentStudentData = studentAppObjects?.[studentKey] ?? {};

            // Prevent dispatch if the data is identical to avoid unnecessary re-renders
            if (JSON.stringify(currentStudentData.schoolChoices) === JSON.stringify(arr)) {
                return;
            }

            const newStudentAppObjects = {
                ...studentAppObjects,
                [studentKey]: {
                    ...currentStudentData,
                    schoolChoices: arr
                }
            };

            // HACK: The global state type for the 'StudentAppObjects' action is incorrect and
            // too restrictive. It doesn't account for the `schoolChoices` property which is
            // not of type `{ appObj: AppObject | null }`. We must cast the payload here to
            // match the incorrect type to avoid a compilation error. The runtime reducer
            // handles this structure correctly.
            dispatch({
                type: "StudentAppObjects",
                studentAppObjects: newStudentAppObjects as unknown as Record<
                    string,
                    Record<string, { appObj: AppObject | null }>
                >
            });
        },
        [dispatch, studentAppObjects, studentKey]
    );

    useEffect(() => {
        if (existing) {
            setSchoolChoices(existing);
            setLoading(false);
        }
    }, [existing]);

    useEffect(() => {
        if (existing) return;
        if (!schoolChoiceDetailDtos || schoolChoiceDetailDtos.length < 1) return;

        const controller = new AbortController();
        const { signal } = controller;

        const fetchData = async (): Promise<void> => {
            await fetchParticipatingSchools(schoolChoiceDetailDtos, dispatchAppObject, signal);
            if (!signal.aborted) {
                setLoading(false);
            }
        };

        void fetchData();

        // Cleanup function to abort the request on unmount
        return (): void => controller.abort();
    }, [dispatchAppObject, existing, schoolChoiceDetailDtos]);

    return (
        <div className="fade-style">
            <div style={{ margin: "15px 0 -25px 0" }}>
                <i>
                    <b>{T({ tKey: "OEApp_SchoolChoices", langStr: lang })}</b>
                </i>
            </div>
            <br />
            <div className="more-info-schools-container">
                {schoolChoices.length > 0 &&
                    schoolChoices.map((school, index) => {
                        const uniqueKey = `school-${school.key}-${index}`;
                        return <SchoolCard key={uniqueKey} school={school} />;
                    })}
                {loading && <LoadingSvg2 />}
            </div>
            <div className="more-info-button-container">
                <button className="main-info-cancel" onClick={() => setExpand?.(false)} type="button">
                    {T({ tKey: "Global_Close", langStr: lang })}
                </button>
            </div>
        </div>
    );
};

export default ExpandInfo;
